import * as React from 'react';
import {
  Clock,
  Folder,
  Home,
  Plus,
  Shield,
  Star,
  Upload,
  UserCheck,
  Users,
} from 'lucide-react';

import type { CatalogFilterKey } from '@/types/Sixes';
import type { SidebarDefinition, SidebarSectionDefinition } from '@/types/AppLayout.types';
import type { SixDetailFilterKey } from '@/types/SixDetail.types';

export interface GetSidebarDefinitionInput {
  pathname: string;
  activeFilters: CatalogFilterKey[];
  activeSixDetailFilters?: SixDetailFilterKey[];
  canCreateSix?: boolean;
  canManageExternalUsers?: boolean;
  externalUsersInvitedByMeActive?: boolean;
}

function isSixesDashboardRoute(pathname: string): boolean {
  return (
    pathname === '/app/sixes' || pathname === '/app/sixes/' || pathname.startsWith('/app/sixes?')
  );
}

function isExternalUsersRoute(pathname: string): boolean {
  return pathname === '/app/external-users' || pathname.startsWith('/app/external-users?');
}

function isUpdatePasswordRoute(pathname: string): boolean {
  return pathname === '/app/update-password' || pathname.startsWith('/app/update-password?');
}

function isSixDetailRoute(pathname: string): boolean {
  return /^\/app\/sixes\/[^/]+/.test(pathname);
}

function isFilterActive(activeFilters: CatalogFilterKey[], filterKey: CatalogFilterKey): boolean {
  return activeFilters.includes(filterKey);
}

function buildLogoNode(): React.ReactNode {
  return (
    <img src="/logos/PortalSIXcorrectlogo.webp" alt="PortalSIX" className="h-auto w-[200px]" />
  );
}

function buildSix3roFooterContent(): React.ReactNode {
  return (
    <a
      href="https://six3ro.com"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Visit SIX3RO"
      className="inline-block"
    >
      <img src="/logos/SIX3ROlogo.webp" alt="SIX3RO" className="h-auto w-full max-w-[140px]" />
    </a>
  );
}

function buildExternalUsersButtonItem(): SidebarSectionDefinition['items'][number] {
  return {
    key: 'sixes.externalUsers',
    label: 'Manage external users',
    icon: <Users className="h-4 w-4" />,
    kind: 'button',
    buttonVariant: 'outline',
    buttonSize: 'md',
    onSelect: { type: 'navigate', pathname: '/app/external-users' },
  };
}

export function getSidebarDefinition(input: GetSidebarDefinitionInput): SidebarDefinition {
  const {
    pathname,
    activeFilters,
    activeSixDetailFilters,
    canCreateSix = false,
    canManageExternalUsers = false,
    externalUsersInvitedByMeActive = false,
  } = input;

  const logoNode = buildLogoNode();
  const six3roFooterContent = buildSix3roFooterContent();

  if (isSixDetailRoute(pathname)) {
    const resolvedDetailFilters = activeSixDetailFilters ?? ['myFilesAndFolders'];

    const detailPrimaryItems: SidebarSectionDefinition['items'] = [
      {
        key: 'detail.uploadFiles',
        label: 'Upload files',
        icon: <Upload className="h-4 w-4" />,
        kind: 'button',
        buttonVariant: 'primary',
        buttonSize: 'md',
        onSelect: { type: 'dispatch', actionKey: 'detail.openUploadDialog' },
      },
      {
        key: 'detail.createNewFolder',
        label: 'Create new folder',
        icon: <Folder className="h-4 w-4" />,
        kind: 'button',
        buttonVariant: 'outline',
        buttonSize: 'md',
        onSelect: { type: 'dispatch', actionKey: 'detail.openCreateFolderDialog' },
      },
    ];
    if (canManageExternalUsers) {
      detailPrimaryItems.push(buildExternalUsersButtonItem());
    }

    const detailSections: SidebarSectionDefinition[] = [
      {
        title: '',
        items: detailPrimaryItems,
      },
      {
        title: 'Filters',
        items: [
          {
            key: 'detail.myFilesAndFolders',
            label: 'My files and folders',
            icon: <Folder className="h-4 w-4" />,
            isActive: resolvedDetailFilters.includes('myFilesAndFolders'),
            onSelect: { type: 'dispatch', actionKey: 'detail.toggleFilter.myFilesAndFolders' },
          },
          {
            key: 'detail.recent',
            label: 'Recent',
            icon: <Clock className="h-4 w-4" />,
            isActive: resolvedDetailFilters.includes('recent'),
            onSelect: { type: 'dispatch', actionKey: 'detail.toggleFilter.recent' },
          },
          {
            key: 'detail.favorites',
            label: 'Favorites',
            icon: <Star className="h-4 w-4" />,
            isActive: resolvedDetailFilters.includes('favorites'),
            onSelect: { type: 'dispatch', actionKey: 'detail.toggleFilter.favorites' },
          },
        ],
      },
    ];

    return {
      logoNode,
      sections: detailSections,
      footer: {
        key: 'detail.securityStatus',
        title: 'Security Status',
        description: 'All files are encrypted and secure',
        icon: <Shield className="h-4 w-4 text-primary" />,
        content: six3roFooterContent,
      },
    };
  }

  if (isSixesDashboardRoute(pathname)) {
    const primaryItems: SidebarSectionDefinition['items'] = [];
    if (canCreateSix) {
      primaryItems.push({
        key: 'sixes.createNewSix',
        label: 'Create new SIX',
        icon: <Plus className="h-4 w-4" />,
        kind: 'button',
        buttonVariant: 'primary',
        buttonSize: 'md',
        onSelect: { type: 'dispatch', actionKey: 'showCreateSixModal' },
      });
    }
    if (canManageExternalUsers) {
      primaryItems.push(buildExternalUsersButtonItem());
    }

    const dashboardSections: SidebarSectionDefinition[] = [
      {
        title: '',
        items: primaryItems,
      },
      {
        title: 'Filters',
        items: [
          {
            key: 'catalog.filter.mySixes',
            label: 'My SIXs',
            icon: <Folder className="h-4 w-4" />,
            isActive: isFilterActive(activeFilters, 'mySixes'),
            onSelect: { type: 'dispatch', actionKey: 'catalog.toggleFilter.mySixes' },
          },
          {
            key: 'catalog.filter.recent',
            label: 'Recent',
            icon: <Clock className="h-4 w-4" />,
            isActive: isFilterActive(activeFilters, 'recent'),
            onSelect: { type: 'dispatch', actionKey: 'catalog.toggleFilter.recent' },
          },
          {
            key: 'catalog.filter.favorites',
            label: 'Favorites',
            icon: <Star className="h-4 w-4" />,
            isActive: isFilterActive(activeFilters, 'favorites'),
            onSelect: { type: 'dispatch', actionKey: 'catalog.toggleFilter.favorites' },
          },
        ],
      },
    ];

    return {
      logoNode,
      sections: dashboardSections,
      footer: {
        key: 'sixes.securityStatus',
        title: 'Security Status',
        description: 'All SIXs are protected with post-quantum encryption',
        icon: <Shield className="h-4 w-4 text-primary" />,
        content: six3roFooterContent,
      },
    };
  }

  if (isExternalUsersRoute(pathname)) {
    return {
      logoNode,
      sections: [
        {
          title: '',
          items: [
            {
              key: 'externalUsers.home',
              label: 'Home',
              icon: <Home className="h-4 w-4" />,
              kind: 'button',
              buttonVariant: 'outline',
              buttonSize: 'md',
              onSelect: { type: 'navigate', pathname: '/app/sixes' },
            },
          ],
        },
        {
          title: 'Filters',
          items: [
            {
              key: 'externalUsers.filter.invitedByMe',
              label: 'Invited by me',
              icon: <UserCheck className="h-4 w-4" />,
              isActive: externalUsersInvitedByMeActive,
              onSelect: { type: 'dispatch', actionKey: 'externalUsers.toggleInvitedByMe' },
            },
          ],
        },
      ],
      footer: {
        key: 'externalUsers.securityStatus',
        title: 'Security Status',
        description: 'All SIXs are protected with post-quantum encryption',
        icon: <Shield className="h-4 w-4 text-primary" />,
        content: six3roFooterContent,
      },
    };
  }

  if (isUpdatePasswordRoute(pathname)) {
    return {
      logoNode,
      sections: [
        {
          title: '',
          items: [
            {
              key: 'settings.home',
              label: 'Home',
              icon: <Home className="h-4 w-4" />,
              kind: 'button',
              buttonVariant: 'outline',
              buttonSize: 'md',
              onSelect: { type: 'navigate', pathname: '/app/sixes' },
            },
          ],
        },
      ],
      footer: {
        key: 'settings.securityStatus',
        title: 'Security Status',
        description: 'All files are encrypted and secure',
        icon: <Shield className="h-4 w-4 text-primary" />,
        content: six3roFooterContent,
      },
    };
  }

  return {
    logoNode,
    sections: [
      {
        title: '',
        items: [
          {
            key: 'sixes.goToDashboard',
            label: 'My SIXs',
            icon: <Folder className="h-4 w-4" />,
            isActive: true,
            onSelect: { type: 'navigate', pathname: '/app/sixes' },
          },
        ],
      },
    ],
  };
}
